import {useRef, useState} from 'react';
import {FmlxTableHeadCell} from 'fmlx-common-ui';
import './MassageTable.scss';
import PropTypes from 'prop-types';
import MessageViewModal from './MessageModal/MessageViewModal';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {modalCreateUpdateEmployeeStateSelector} from '../../stores/selectors/manageEmployeeStateSelector';
import EssSimpleTable from '../../components/MessageTable/TableColumn/EssSimpleTable';
import {usePagination} from '../../hooks/usePagination';
import {useDataProcessor} from '../../hooks/useDataProcessor';
import {useColumnFormat} from '../../hooks/useColumnFormat';
import {useRenderTableData} from '../../hooks/useRenderTableData';
import {ColumnAccessorEnum, ERoleId, MessageConfig, PaginationEnum, StatusConditionEnum} from '../../const/dataEnum';
import {updateMessageIsRead} from '../../stores/business/messageInboxBusiness';

function createFilteringHandlers(onFilterChange, senderRef, receiverRef, dropdownRef) {
  const handleSenderInputChange = () => {
    const senderValue = senderRef.current ? senderRef.current.value : '';
    onFilterChange('sender', senderValue);
  };

  const handleReceiverInputChange = () => {
    const receiverValue = receiverRef.current ? receiverRef.current.value : '';
    onFilterChange('receiver', receiverValue);
  };

  const handleDropdownChange = () => {
    const dropdownValue = dropdownRef.current ? dropdownRef.current.value : '';
    onFilterChange('dropdown', dropdownValue);
  };

  return {
    handleSenderInputChange,
    handleReceiverInputChange,
    handleDropdownChange,
  };
}

export default function MessageContent({selectedTab, message, onDataRefresh}) {
  const {userAuthRoleID} = useSelector(modalCreateUpdateEmployeeStateSelector);
  const [open, setOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filters, setFilters] = useState({sender: '', receiver: '', dropdown: []});
  const {pathname} = useLocation();
  const senderInputRef = useRef(null);
  const receiverInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const {dataState} = useDataProcessor(message, MessageConfig, selectedTab);
  const {columnFormat} = useColumnFormat(ColumnAccessorEnum, StatusConditionEnum);
  const {dataPerColumns} = useRenderTableData(pathname, selectedTab, handleOnClick, 'custom-action');

  const {
    page,
    setPage,
    rowsPerPage,
    slicedData,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSort,
    pageOptions,
    MemoizedEmptyRows,
  } = usePagination(dataState, filters, PaginationEnum.PAGE_OPTIONS);

  const {handleSenderInputChange, handleReceiverInputChange} = createFilteringHandlers(
    onFilterChange,
    senderInputRef,
    receiverInputRef,
    dropdownRef
  );

  function onFilterChange(field, value) {
    setFilters((prev) => ({...prev, [field]: value}));
    setPage(0);
  }

  async function handleOnClick(message) {
    const messageId = message?.id;
    if (messageId) {
      await updateMessageIsRead(messageId, true);
    }
    setSelectedMessage(message);
    setOpen(true);
  }

  const handleOnCloseModal = () => {
    setOpen(false);
    setSelectedMessage(null);
  };

  return (
    <div className="Dropbox-Table">
      <EssSimpleTable
        columns={columnFormat}
        columnData={dataPerColumns}
        onSort={handleSort}
        classNameBy={userAuthRoleID}
        targetValue={ERoleId.TEAM_MEMBER}
        columnFilterRefs={{
          sender: senderInputRef,
          receiver: receiverInputRef,
        }}
        columnFilterHandlers={{
          sender: handleSenderInputChange,
          receiver: handleReceiverInputChange,
        }}
        filteredData={slicedData}
        renderEmptyData={MemoizedEmptyRows}
        dataCount={dataState}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onPageOption={pageOptions}
      />
      {selectedMessage && (
        <MessageViewModal
          open={open}
          onClose={handleOnCloseModal}
          onDataRefresh={onDataRefresh}
          selectedMessage={selectedMessage}
          condition={pathname.includes('/All')}
        />
      )}
    </div>
  );
}

FmlxTableHeadCell.propTypes = {
  sortDisabled: PropTypes.bool,
  onClickSort: PropTypes.func,
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};
